import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PageShell from "@/components/feature/PageShell";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import WorkCard from "@/components/base/WorkCard";
import WorkRow from "@/components/base/WorkRow";
import type { WorkType } from "@/types/work";
import { getHotWorks, searchWorks, workTypeLabel } from "@/lib/workUtils";

type FilterType = WorkType | "all";

const filterTabs: { key: FilterType; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "movie", label: "电影" },
  { key: "tv", label: "电视剧" },
];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const [keyword, setKeyword] = useState(q);
  const [filter, setFilter] = useState<FilterType>("all");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setKeyword(q);
  }, [q]);

  const results = useMemo(() => searchWorks(q, filter), [q, filter]);
  const hot = useMemo(() => getHotWorks(4), []);

  useEffect(() => {
    if (!q) return;
    setLoading(true);
    const timer = setTimeout(() => setLoading(false), 300);
    return () => clearTimeout(timer);
  }, [q, filter]);

  const submit = (value: string) => {
    const kw = value.trim();
    setParams(kw ? { q: kw } : {});
  };

  const hasQuery = q.trim().length > 0;

  useDocumentMeta({
    title: q ? `“${q}”的搜索结果-妻子的姐姐` : "影视搜索-妻子的姐姐",
    description:
      "在妻子的姐姐站内搜索影视作品、演员与导演，快速查询电影和电视剧的剧情介绍、演员信息与更新资讯。",
    keywords: "妻子的姐姐,影视搜索,影视查询,剧情介绍,演员信息",
  });

  return (
    <PageShell>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full px-4 py-8 md:px-8 md:py-10">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />
          <h1 className="mt-4 font-heading text-2xl font-extrabold tracking-tight text-foreground-950 md:text-4xl">
            影视搜索
            <span className="ml-3 align-middle text-sm font-medium text-foreground-400">
              妻子的姐姐
            </span>
          </h1>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submit(keyword);
            }}
            className="mt-5 flex flex-col gap-3 sm:flex-row"
          >
            <div className="flex flex-1 items-center gap-3 rounded-md border border-background-300 bg-background-50 px-4 py-3 transition-colors focus-within:border-primary-400">
              <span className="flex h-5 w-5 items-center justify-center text-foreground-400">
                <i className="ri-search-line text-lg" />
              </span>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="输入影视名称、演员或导演关键词"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              {keyword && (
                <button
                  type="button"
                  onClick={() => {
                    setKeyword("");
                    submit("");
                  }}
                  aria-label="清空"
                  className="flex h-6 w-6 items-center justify-center text-foreground-400 transition-colors hover:text-foreground-700"
                >
                  <i className="ri-close-circle-line" />
                </button>
              )}
            </div>
            <button
              type="submit"
              className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-8 py-3 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-600"
            >
              <i className="ri-search-line text-base" />
              搜索
            </button>
          </form>
        </div>
      </div>

      <div className="mx-auto w-full px-4 py-8 md:px-8 md:py-10">
        {hasQuery ? (
          <>
            <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-foreground-600">
                {loading ? (
                  <>
                    <i className="ri-loader-4-line mr-1 inline-block animate-spin" />
                    正在搜索“{q}”…
                  </>
                ) : (
                  <>
                    关键词“<span className="font-bold text-foreground-950">{q}</span>”共找到{" "}
                    <span className="font-bold text-accent-700">{results.length}</span> 条结果
                  </>
                )}
              </p>
              <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
                {filterTabs.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setFilter(tab.key)}
                    className={`whitespace-nowrap rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${
                      filter === tab.key
                        ? "bg-primary-700 text-background-50"
                        : "text-foreground-600 hover:text-primary-700"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
                {Array.from({ length: 5 }).map((_, index) => (
                  <div
                    key={index}
                    className="animate-pulse overflow-hidden rounded-lg border border-background-200"
                  >
                    <div className="aspect-[2/3] w-full bg-background-200" />
                    <div className="space-y-2 p-4">
                      <div className="h-3.5 w-3/4 rounded bg-background-200" />
                      <div className="h-3 w-1/2 rounded bg-background-200" />
                    </div>
                  </div>
                ))}
              </div>
            ) : results.length === 0 ? (
              <EmptyState
                title="没有找到匹配的内容"
                description={`没有与“${q}”相关的影视作品，试试更换关键词，或从下方热门推荐中挑一部开始浏览。`}
                icon="ri-search-eye-line"
              />
            ) : (
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
                {results.map((work) => (
                  <WorkCard key={work.id} work={work} />
                ))}
              </div>
            )}

            {!loading && results.length === 0 && (
              <div className="mt-10">
                <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <span className="h-4 w-1.5 rounded-full bg-accent-500" />
                  热门影视推荐
                </h2>
                <div className="mt-5 grid grid-cols-2 gap-4 md:grid-cols-4">
                  {hot.map((work) => (
                    <WorkCard key={work.id} work={work} />
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="space-y-10">
            <EmptyState
              title="输入关键词，开始查找影视资料"
              description="支持影视名称、演员姓名、导演与类型的模糊搜索。也可以直接浏览热门作品或进入分类页筛选。"
              icon="ri-movie-2-line"
            />
            <div>
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <span className="h-4 w-1.5 rounded-full bg-accent-500" />
                大家正在看
              </h2>
              <div className="mt-5 space-y-3">
                {hot.map((work) => (
                  <WorkRow key={work.id} work={work} />
                ))}
              </div>
              <p className="mt-4 text-xs text-foreground-500">
                提示：点击上方卡片或使用顶部搜索框，即可查看{workTypeLabel("movie")}与
                {workTypeLabel("tv")}的详细剧情与演员信息。
              </p>
            </div>
          </div>
        )}
      </div>
    </PageShell>
  );
}